import React from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowLeft, Download, ExternalLink } from "lucide-react";

export default function BuildingBridgeToolPage() {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>Building the Bridge — Reflection Workbook | ALIGN&bull;</title>
        <meta name="description" content="A free 59-page reflection workbook for parents raising children with executive function differences. By ALIGN Praxis." />
      </Helmet>

      <div className="min-h-screen flex flex-col" style={{ backgroundColor: "#F7F5F0" }} data-testid="building-bridge-tool-page">
        <header className="bg-white/70 backdrop-blur-xl border-b border-white/20 sticky top-0 z-40">
          <div className="max-w-3xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
            <button onClick={() => navigate("/")} className="font-['Cormorant_Garamond'] text-2xl font-light tracking-tight text-[#1A1C19] hover:text-[#4A6741] transition-colors">
              ALIGN<span className="text-[#4A6741]">&bull;</span>
            </button>
            <button onClick={() => navigate("/")} className="text-xs text-[#5C635A] hover:text-[#4A6741] transition-colors inline-flex items-center gap-2">
              <ArrowLeft size={12} /> Back to site
            </button>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center px-6 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-xl"
          >
            <div className="text-center mb-10">
              <p className="text-xs uppercase tracking-[0.3em] font-medium text-[#4A6741] mb-4">
                Free Resource
              </p>
              <h1 className="font-['Cormorant_Garamond'] text-3xl md:text-4xl font-light tracking-tight text-[#1A1C19] mb-2 leading-[1.15]">
                Building the
                <br />
                <span className="italic text-[#4A6741]">Bridge</span>
              </h1>
              <p className="text-sm font-light text-[#5C635A] mt-4 max-w-md mx-auto leading-relaxed">
                A 59-page reflection workbook for parents raising children with
                executive function differences. Scaffolding strategies, reflection
                prompts, and practical frameworks — all in one printable guide.
              </p>
              <p className="font-['Cormorant_Garamond'] text-lg italic text-[#4A6741] mt-6">
                "Scaffolding is not the opposite of accountability.
                It is the pathway to accountability."
              </p>
            </div>

            <div className="space-y-3 mb-10">
              <a
                href="https://customer-assets.emergentagent.com/job_wellness-journey-231/artifacts/ausuzf4c_Building_the_Bridge_ALIGN_Praxis.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="glass-card p-5 flex items-center gap-4 group hover:border-[#4A6741]/20"
                data-testid="tool-download-workbook"
              >
                <div className="w-12 h-12 rounded-2xl bg-[#4A6741]/10 flex items-center justify-center shrink-0 group-hover:bg-[#4A6741]/15 transition-colors">
                  <Download size={20} className="text-[#4A6741]" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#1A1C19]">Download PDF</p>
                  <p className="text-xs font-light text-[#5C635A]">59-page print-ready edition</p>
                </div>
              </a>

              <a
                href="/tools/building-the-bridge/preview"
                className="glass-card p-5 flex items-center gap-4 group hover:border-[#4A6741]/20"
                data-testid="tool-preview-workbook"
              >
                <div className="w-12 h-12 rounded-2xl bg-[#4A6741]/10 flex items-center justify-center shrink-0 group-hover:bg-[#4A6741]/15 transition-colors">
                  <ExternalLink size={20} className="text-[#4A6741]" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-[#1A1C19]">Preview Online</p>
                  <p className="text-xs font-light text-[#5C635A]">Scroll through all 59 pages in your browser</p>
                </div>
              </a>
            </div>

            <div className="text-center">
              <p className="text-xs font-light text-[#5C635A] mb-4">
                Want to know more about how ALIGN&bull; supports families?
              </p>
              <button onClick={() => navigate("/")} className="btn-pill text-[11px]">
                Visit ALIGN&bull;
              </button>
            </div>
          </motion.div>
        </main>

        <footer className="text-center py-6 px-6">
          <p className="text-xs font-light text-[#5C635A]/50">
            &copy; {new Date().getFullYear()} ALIGN&bull; Integration Psychology Practice &middot; Perth
          </p>
        </footer>
      </div>
    </>
  );
}
